/** Configuration — Limits & Thresholds — requirements, purposes, numeric settings, sample rules */

export const LIMITS_ROUTE = '/admin/configuration/limits';

/** @deprecated No Rule State in Limits & Thresholds. Kept for legacy callers. */
export const RULE_STATE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

export const REQUIREMENT_OPTIONS = [
  { value: 'quantity_limit', label: 'Quantity Limit', measure: 'quantity', measureLabel: 'Quantity' },
  { value: 'percentage_threshold', label: 'Percentage Threshold', measure: 'percentage', measureLabel: 'Percentage' },
  { value: 'duration_limit', label: 'Duration Limit', measure: 'duration', measureLabel: 'Duration' },
  { value: 'distance_limit', label: 'Distance Limit', measure: 'distance', measureLabel: 'Distance' },
  { value: 'size_limit', label: 'Size Limit', measure: 'size', measureLabel: 'Size' },
];

export const LIMIT_TYPE_OPTIONS = [
  { value: 'single_value', label: 'Single Value' },
  { value: 'min_max_range', label: 'Min–Max Range' },
];

export const SINGLE_VALUE_OPERATOR_OPTIONS = [
  { value: 'equal_to', label: 'Equal To' },
  { value: 'at_least', label: 'At Least' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'at_most', label: 'At Most' },
  { value: 'less_than', label: 'Less Than' },
];

export const RANGE_OPERATOR_OPTIONS = [
  { value: 'within_range', label: 'Within Range' },
];

export const BOUNDARY_OPTIONS = [
  { value: 'included', label: 'Included' },
  { value: 'excluded', label: 'Excluded' },
];

export const UNIT_OPTIONS = [
  { value: 'count', label: 'Count' },
  { value: 'percent', label: '%' },
  { value: 'seconds', label: 'Seconds' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'calendar_days', label: 'Calendar Days' },
  { value: 'm', label: 'm' },
  { value: 'km', label: 'km' },
  { value: 'bytes', label: 'Bytes' },
  { value: 'kb', label: 'KB' },
  { value: 'mb', label: 'MB' },
  { value: 'gb', label: 'GB' },
];

const UNIT_TO_BASE = {
  count: 1,
  percent: 1,
  points: 1,
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
  m: 1,
  km: 1000,
  bytes: 1,
  kb: 1000,
  mb: 1000000,
  gb: 1000000000,
};

/** Calendar Days has no fixed conversion to elapsed duration units. */
const NON_CONVERTIBLE_UNITS = new Set(['calendar_days']);

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'is', label: 'Is', multi: false },
  { value: 'is_not', label: 'Is Not', multi: false },
  { value: 'is_any_of', label: 'Is Any Of', multi: true },
  { value: 'is_not_any_of', label: 'Is Not Any Of', multi: true },
];

export const CONDITION_FIELD_OPTIONS = [
  { value: 'module', label: 'Module' },
  { value: 'record_type', label: 'Record Type' },
];

export const CONDITION_MODULE_OPTIONS = [
  {
    value: 'posts',
    label: 'Posts',
    reference: 'MOD-POSTS',
    recordTypes: [
      { value: 'post', label: 'Post', reference: 'RT-POSTS-POST' },
      { value: 'post_image', label: 'Post Image', reference: 'RT-POSTS-IMAGE' },
    ],
  },
  {
    value: 'reviews',
    label: 'Reviews',
    reference: 'MOD-REVIEWS',
    recordTypes: [
      { value: 'review', label: 'Review', reference: 'RT-REVIEWS-REVIEW' },
      { value: 'review_reply', label: 'Review Reply', reference: 'RT-REVIEWS-REPLY' },
    ],
  },
  {
    value: 'messages',
    label: 'Messages',
    reference: 'MOD-MESSAGES',
    recordTypes: [{ value: 'message', label: 'Message', reference: 'RT-MESSAGES-MESSAGE' }],
  },
  {
    value: 'quotes',
    label: 'Quotes',
    reference: 'MOD-QUOTES',
    recordTypes: [{ value: 'quote', label: 'Quote', reference: 'RT-QUOTES-QUOTE' }],
  },
  {
    value: 'support_tickets',
    label: 'Support Tickets',
    reference: 'MOD-TICKETS',
    recordTypes: [{ value: 'ticket', label: 'Ticket', reference: 'RT-TICKETS-TICKET' }],
  },
  {
    value: 'customers',
    label: 'Customers',
    reference: 'MOD-CUSTOMERS',
    recordTypes: [{ value: 'customer_account', label: 'Customer Account', reference: 'RT-CUSTOMERS-ACCOUNT' }],
  },
  {
    value: 'businesses',
    label: 'Businesses',
    reference: 'MOD-BUSINESSES',
    recordTypes: [{ value: 'business_account', label: 'Business Account', reference: 'RT-BUSINESSES-ACCOUNT' }],
  },
  {
    value: 'compliance',
    label: 'Compliance',
    reference: 'MOD-COMPLIANCE',
    recordTypes: [
      { value: 'licence_record', label: 'Licence Record', reference: 'RT-COMPLIANCE-LICENCE' },
      { value: 'badge_record', label: 'Badge Record', reference: 'RT-COMPLIANCE-BADGE' },
    ],
  },
  {
    value: 'moderation',
    label: 'Moderation',
    reference: 'MOD-MODERATION',
    recordTypes: [{ value: 'moderation_case', label: 'Moderation Case', reference: 'RT-MODERATION-CASE' }],
  },
  {
    value: 'billing_subscriptions',
    label: 'Billing & Subscriptions',
    reference: 'MOD-BILLING',
    recordTypes: [{ value: 'invoice', label: 'Invoice', reference: 'RT-BILLING-INVOICE' }],
  },
];

const CONTEXT_FIELDS = ['module', 'record_type'];

export const LIMIT_FOR_OPTIONS = [
  {
    value: 'record_count',
    label: 'Record Count',
    reference: 'sample-record-count',
    requirement: 'quantity_limit',
    meaning: 'Actual record total for one Module and Record Type. Single Value or Min–Max Range.',
    units: ['count'],
    limitTypes: ['single_value', 'min_max_range'],
    operators: ['equal_to', 'at_least', 'greater_than', 'at_most', 'less_than'],
    integersOnly: true,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 0,
    conditionFields: CONTEXT_FIELDS,
    available: true,
  },
  {
    value: 'record_age',
    label: 'Record Age',
    reference: 'sample-record-age',
    requirement: 'duration_limit',
    meaning: 'Elapsed time from the record’s valid creation timestamp to the comparison. Single Value or Min–Max Range using elapsed Units.',
    units: ['seconds', 'minutes', 'hours', 'days'],
    limitTypes: ['single_value', 'min_max_range'],
    operators: ['equal_to', 'at_least', 'greater_than', 'at_most', 'less_than'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 2,
    conditionFields: CONTEXT_FIELDS,
    available: true,
  },
  {
    value: 'record_share',
    label: 'Record Share',
    reference: 'sample-record-share',
    requirement: 'percentage_threshold',
    meaning: 'Actual Record Type count ÷ actual Module total × 100. Single Value or Min–Max Range.',
    units: ['percent'],
    limitTypes: ['single_value', 'min_max_range'],
    operators: ['equal_to', 'at_least', 'greater_than', 'at_most', 'less_than'],
    integersOnly: false,
    min: 0,
    max: 100,
    exclusiveMin: false,
    maxDecimals: 2,
    conditionFields: CONTEXT_FIELDS,
    available: true,
  },
  {
    value: 'location_distance',
    label: 'Location Distance',
    reference: 'sample-location-distance',
    requirement: 'distance_limit',
    meaning: 'Direct geographic distance between the record location and reference location. Single Value or Min–Max Range.',
    units: ['m', 'km'],
    limitTypes: ['single_value', 'min_max_range'],
    operators: ['equal_to', 'at_least', 'greater_than', 'at_most', 'less_than'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 3,
    conditionFields: CONTEXT_FIELDS,
    available: true,
  },
  {
    value: 'total_record_size',
    label: 'Total Record Size',
    reference: 'sample-total-record-size',
    requirement: 'size_limit',
    meaning: 'Total size of records’ own data within one Module and Record Type. Single Value or Min–Max Range.',
    units: ['bytes', 'kb', 'mb', 'gb'],
    limitTypes: ['single_value', 'min_max_range'],
    operators: ['equal_to', 'at_least', 'greater_than', 'at_most', 'less_than'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 3,
    bytesIntegersOnly: true,
    conditionFields: CONTEXT_FIELDS,
    available: true,
  },
  {
    value: 'initial_send_delay',
    label: 'Initial Send Delay',
    reference: 'sample-initial-send-delay',
    requirement: 'duration_limit',
    meaning: 'Duration before the first send. Single Value; Equal To; elapsed duration of zero or more.',
    units: ['seconds', 'minutes', 'hours', 'days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 2,
    conditionFields: [],
    available: true,
  },
  {
    value: 'repeat_send_interval',
    label: 'Repeat Send Interval',
    reference: 'sample-repeat-send-interval',
    requirement: 'duration_limit',
    meaning: 'Duration between repeat sends. Single Value; Equal To; elapsed duration greater than zero.',
    units: ['seconds', 'minutes', 'hours', 'days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: false,
    min: 0,
    exclusiveMin: true,
    maxDecimals: 2,
    conditionFields: [],
    available: true,
  },
  {
    value: 'maximum_sends',
    label: 'Maximum Sends',
    reference: 'sample-maximum-sends',
    requirement: 'quantity_limit',
    meaning: 'Maximum total sends, including the first send and repeats. Single Value; At Most; whole Count of zero or more.',
    units: ['count'],
    limitTypes: ['single_value'],
    operators: ['at_most'],
    integersOnly: true,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 0,
    conditionFields: [],
    available: true,
  },
  {
    value: 'status_change_period',
    label: 'Status Change Period',
    reference: 'sample-status-change-period',
    requirement: 'duration_limit',
    meaning: 'Duration from the selected starting timestamp. Single Value; Equal To; elapsed duration of zero or more.',
    units: ['seconds', 'minutes', 'hours', 'days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 2,
    conditionFields: [],
    available: true,
  },
  {
    value: 'status_change_retry_limit',
    label: 'Status Change Retry Limit',
    reference: 'sample-status-change-retry-limit',
    requirement: 'quantity_limit',
    meaning: 'Maximum additional technical attempts after the initial attempt. Single Value; At Most; whole Count of zero or more.',
    units: ['count'],
    limitTypes: ['single_value'],
    operators: ['at_most'],
    integersOnly: true,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 0,
    conditionFields: [],
    available: true,
  },
  {
    value: 'status_change_retry_interval',
    label: 'Status Change Retry Interval',
    reference: 'sample-status-change-retry-interval',
    requirement: 'duration_limit',
    meaning: 'Duration before another technical attempt. Single Value; Equal To; elapsed duration greater than zero.',
    units: ['seconds', 'minutes', 'hours', 'days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: false,
    min: 0,
    exclusiveMin: true,
    maxDecimals: 2,
    conditionFields: [],
    available: true,
  },
  {
    value: 'admin_idle_timeout',
    label: 'Admin Idle Timeout',
    reference: 'sample-admin-idle-timeout',
    requirement: 'duration_limit',
    meaning: 'Permitted Admin idle duration. Single Value; Equal To; elapsed duration of zero or more, subject to confirmed source restrictions.',
    units: ['seconds', 'minutes', 'hours', 'days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 2,
    conditionFields: [],
    available: true,
  },
  {
    value: 'customer_idle_timeout',
    label: 'Customer Idle Timeout',
    reference: 'sample-customer-idle-timeout',
    requirement: 'duration_limit',
    meaning: 'Permitted Customer idle duration. Single Value; Equal To; elapsed duration of zero or more, subject to confirmed source restrictions.',
    units: ['seconds', 'minutes', 'hours', 'days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 2,
    conditionFields: [],
    available: true,
  },
  {
    value: 'business_idle_timeout',
    label: 'Business Idle Timeout',
    reference: 'sample-business-idle-timeout',
    requirement: 'duration_limit',
    meaning: 'Permitted Business idle duration. Single Value; Equal To; elapsed duration of zero or more, subject to confirmed source restrictions.',
    units: ['seconds', 'minutes', 'hours', 'days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 2,
    conditionFields: [],
    available: true,
  },
  {
    value: 'absolute_session_lifetime',
    label: 'Absolute Session Lifetime',
    reference: 'sample-absolute-session-lifetime',
    requirement: 'duration_limit',
    meaning: 'Maximum session lifetime. Single Value; Equal To; elapsed duration of zero or more, subject to confirmed source restrictions.',
    units: ['seconds', 'minutes', 'hours', 'days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 2,
    conditionFields: [],
    available: true,
  },
  {
    value: 'password_reset_link_expiry',
    label: 'Password Reset Link Expiry',
    reference: 'sample-password-reset-link-expiry',
    requirement: 'duration_limit',
    meaning: 'Validity of an issued password-reset link. Single Value; Equal To; elapsed duration of zero or more, subject to confirmed source restrictions.',
    units: ['seconds', 'minutes', 'hours', 'days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 2,
    conditionFields: [],
    available: true,
  },
  {
    value: 'team_temporary_password_expiry',
    label: 'Team Temporary Password Expiry',
    reference: 'sample-team-temporary-password-expiry',
    requirement: 'duration_limit',
    meaning: 'Validity of an unused temporary password. Single Value; Equal To; elapsed duration. Preserve the confirmed 24-hour period.',
    units: ['hours'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: false,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 2,
    fixedValue: '24',
    fixedUnit: 'hours',
    conditionFields: [],
    available: true,
  },
  {
    value: 'compliance_information_response_period',
    label: 'Compliance Information Response Period',
    reference: 'sample-compliance-info-response-period',
    requirement: 'duration_limit',
    meaning: 'Single Value; Equal To; Calendar Days; whole Value of zero or more. Initial configured Value: 14. Retained when the Compliance case is created.',
    units: ['calendar_days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: true,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 0,
    initialValue: '14',
    conditionFields: [],
    available: true,
  },
  {
    value: 'fraud_security_information_response_period',
    label: 'Fraud & Security Information Response Period',
    reference: 'sample-fs-info-response-period',
    requirement: 'duration_limit',
    meaning: 'Separate purpose for new F&S information requests. Single Value; Equal To; Calendar Days; whole Value of zero or more. Initial configured Value: 14.',
    units: ['calendar_days'],
    limitTypes: ['single_value'],
    operators: ['equal_to'],
    integersOnly: true,
    min: 0,
    exclusiveMin: false,
    maxDecimals: 0,
    initialValue: '14',
    conditionFields: [],
    available: true,
  },
];

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  return options.find((option) => option.value === value)?.label ?? String(value);
}

export function namesMatch(a, b) {
  return String(a || '').trim().toLowerCase().replace(/\s+/g, ' ') === String(b || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function operatorIsMulti(operator) {
  return CONDITION_OPERATOR_OPTIONS.find((option) => option.value === operator)?.multi === true;
}

export function getRequirementDefinition(value) {
  return REQUIREMENT_OPTIONS.find((option) => option.value === value) || null;
}

export function getLimitForDefinition(value) {
  return LIMIT_FOR_OPTIONS.find((option) => option.value === value) || null;
}

export function getRequirementLabel(value) {
  return labelFromOptions(REQUIREMENT_OPTIONS, value);
}

export function getLimitForLabel(value) {
  const definition = getLimitForDefinition(value);
  if (!definition) return value ? `${value} Unavailable` : '';
  return definition.available === false ? `${definition.label} Unavailable` : definition.label;
}

export function getMeasureLabel(requirement) {
  return getRequirementDefinition(requirement)?.measureLabel || '';
}

export function getLimitTypeLabel(value) {
  return labelFromOptions(LIMIT_TYPE_OPTIONS, value);
}

export function getOperatorLabel(value) {
  return labelFromOptions([...SINGLE_VALUE_OPERATOR_OPTIONS, ...RANGE_OPERATOR_OPTIONS], value);
}

export function getUnitLabel(value) {
  return labelFromOptions(UNIT_OPTIONS, value);
}

export function getRuleStateLabel(value) {
  const mapped = String(value || '').toLowerCase() === 'inactive' || String(value || '').toLowerCase() === 'draft' ? 'paused' : String(value || '').toLowerCase();
  return labelFromOptions(RULE_STATE_OPTIONS, mapped);
}

export function getLimitForOptions(requirement) {
  if (!requirement) return [];
  return LIMIT_FOR_OPTIONS.filter((option) => option.requirement === requirement && option.available !== false)
    .map(({ value, label }) => ({ value, label }));
}

export function getCompatibleLimitTypes(limitFor) {
  const definition = getLimitForDefinition(limitFor);
  if (!definition) return [];
  return LIMIT_TYPE_OPTIONS.filter((option) => definition.limitTypes.includes(option.value));
}

export function getCompatibleOperators(limitFor, limitType) {
  if (limitType === 'min_max_range') return RANGE_OPERATOR_OPTIONS;
  const definition = getLimitForDefinition(limitFor);
  if (!definition) return [];
  return SINGLE_VALUE_OPERATOR_OPTIONS.filter((option) => definition.operators.includes(option.value));
}

export function getCompatibleUnits(limitFor) {
  const definition = getLimitForDefinition(limitFor);
  if (!definition) return [];
  return UNIT_OPTIONS.filter((option) => definition.units.includes(option.value));
}

export function getPurposeMeaning(limitFor) {
  return getLimitForDefinition(limitFor)?.meaning || '';
}

export function getPurposeReference(limitFor) {
  return getLimitForDefinition(limitFor)?.reference || '';
}

export function getAvailableConditionFields(limitFor) {
  const fields = getLimitForDefinition(limitFor)?.conditionFields || [];
  return CONDITION_FIELD_OPTIONS.filter((option) => fields.includes(option.value));
}

export function getConditionFieldLabel(value) {
  return labelFromOptions(CONDITION_FIELD_OPTIONS, value);
}

export function getConditionOperatorLabel(value) {
  return labelFromOptions(CONDITION_OPERATOR_OPTIONS, value);
}

function allRecordTypeOptions() {
  return CONDITION_MODULE_OPTIONS.flatMap((module) =>
    module.recordTypes.map((recordType) => ({
      value: `${module.value}:${recordType.value}`,
      label: `${recordType.label} (${module.label})`,
      module: module.value,
      recordType: recordType.value,
      reference: recordType.reference,
    })),
  );
}

export function getConditionValueOptions(field, conditions = []) {
  if (field === 'module') {
    return CONDITION_MODULE_OPTIONS.map((module) => ({ value: module.value, label: module.label }));
  }
  if (field !== 'record_type') return [];
  const moduleValues = (conditions || [])
    .filter((condition) => condition.field === 'module' && ['is', 'is_any_of'].includes(condition.operator))
    .flatMap((condition) => condition.values || []);
  const options = allRecordTypeOptions();
  if (!moduleValues.length) return options;
  return options.filter((option) => moduleValues.includes(option.module));
}

export function getConditionValueLabel(field, value) {
  return labelFromOptions(getConditionValueOptions(field, []), value) || String(value || '');
}

export function recordTypeModule(value) {
  return String(value || '').split(':')[0] || '';
}

export function nextLimitRuleId(allRules) {
  const nums = (allRules || []).map((rule) => {
    const match = String(rule.id || '').match(/^(?:LTH|LIM)-(\d+)$/i);
    return match ? parseInt(match[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `LTH-${String(max + 1).padStart(4, '0')}`;
}

export function createEmptyCondition() {
  return { id: `cond-${Date.now()}`, field: '', operator: '', values: [] };
}

export function createEmptyLimitForm() {
  return {
    id: '',
    ruleName: '',
    description: '',
    requirement: '',
    limitFor: '',
    measure: '',
    limitType: '',
    operator: '',
    value: '',
    minimum: '',
    maximum: '',
    minimumBoundary: '',
    maximumBoundary: '',
    unit: '',
    conditions: [],
    changeReason: '',
    dateAdded: '',
    addedBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
  };
}

function normalizeCondition(raw) {
  const values = Array.isArray(raw?.values)
    ? raw.values.filter(Boolean)
    : raw?.value
      ? [raw.value]
      : [];
  return {
    id: raw?.id || `cond-${Math.random().toString(36).slice(2, 8)}`,
    field: raw?.field || '',
    operator: raw?.operator || '',
    values,
  };
}

export function applyLimitDerivedFields(form) {
  const next = { ...form };
  const requirement = getRequirementDefinition(next.requirement);
  next.measure = requirement?.measure || '';
  const purpose = getLimitForDefinition(next.limitFor);
  if (purpose && purpose.requirement !== next.requirement) next.limitFor = '';
  const resolvedPurpose = getLimitForDefinition(next.limitFor);
  const types = getCompatibleLimitTypes(next.limitFor);
  if (types.length === 1) next.limitType = types[0].value;
  if (next.limitType === 'min_max_range') {
    next.operator = 'within_range';
    next.value = '';
  } else {
    next.minimum = '';
    next.maximum = '';
    next.minimumBoundary = '';
    next.maximumBoundary = '';
  }
  const operators = getCompatibleOperators(next.limitFor, next.limitType);
  if (operators.length === 1) next.operator = operators[0].value;
  const units = getCompatibleUnits(next.limitFor);
  if (units.length === 1) next.unit = units[0].value;
  if (resolvedPurpose?.fixedUnit) next.unit = resolvedPurpose.fixedUnit;
  if (resolvedPurpose?.fixedValue != null && next.limitType === 'single_value') {
    next.value = String(resolvedPurpose.fixedValue);
  }
  if (next.limitType !== 'min_max_range' && next.limitType !== 'single_value') {
    next.value = '';
    next.minimum = '';
    next.maximum = '';
    next.minimumBoundary = '';
    next.maximumBoundary = '';
  }
  return next;
}

export function applyLimitSelectionChange(form, field, value) {
  let next = { ...form, [field]: value };
  if (field === 'requirement') {
    next.limitFor = '';
    next.limitType = '';
    next.operator = '';
    next.value = '';
    next.minimum = '';
    next.maximum = '';
    next.minimumBoundary = '';
    next.maximumBoundary = '';
    next.unit = '';
    next.conditions = [];
  }
  if (field === 'limitFor') {
    next.limitType = '';
    next.operator = '';
    next.value = '';
    next.minimum = '';
    next.maximum = '';
    next.minimumBoundary = '';
    next.maximumBoundary = '';
    next.unit = '';
    next.conditions = [];
    const purpose = getLimitForDefinition(value);
    if (purpose?.initialValue != null) next.value = String(purpose.initialValue);
    if (purpose?.fixedValue != null) next.value = String(purpose.fixedValue);
  }
  if (field === 'limitType') {
    next.operator = '';
    next.value = '';
    next.minimum = '';
    next.maximum = '';
    next.minimumBoundary = '';
    next.maximumBoundary = '';
  }
  if (field === 'unit') {
    next.value = '';
    next.minimum = '';
    next.maximum = '';
  }
  return applyLimitDerivedFields(next);
}

export function normalizeLimitForm(raw = {}) {
  const purpose = getLimitForDefinition(raw.limitFor);
  let value = raw.value != null ? String(raw.value) : '';
  if (!value && purpose?.initialValue != null && !raw.id) value = String(purpose.initialValue);
  if (purpose?.fixedValue != null) value = String(purpose.fixedValue);
  const derived = applyLimitDerivedFields({
    ...createEmptyLimitForm(),
    ...raw,
    requirement: raw.requirement || '',
    limitFor: raw.limitFor || '',
    limitType: raw.limitType === 'usage_cap' || raw.limitType === 'rate_limit' || raw.limitType === 'retry_limit' ? '' : raw.limitType,
    operator: raw.operator || '',
    value,
    minimum: raw.minimum != null ? String(raw.minimum) : '',
    maximum: raw.maximum != null ? String(raw.maximum) : '',
    minimumBoundary: raw.minimumBoundary || '',
    maximumBoundary: raw.maximumBoundary || '',
    unit: purpose?.fixedUnit || raw.unit || '',
    conditions: Array.isArray(raw.conditions) ? raw.conditions.map(normalizeCondition) : [],
  });
  return {
    ...createEmptyLimitForm(),
    ...raw,
    ...derived,
    ruleName: raw.ruleName || '',
    description: raw.description || '',
    changeReason: raw.changeReason || '',
    dateAdded: raw.dateAdded || raw.createdOn || '',
    addedBy: raw.addedBy || raw.createdBy || '',
    lastUpdated: raw.lastUpdated || raw.modifiedOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.modifiedBy || '',
  };
}

export function decimalPlaces(value) {
  const text = String(value || '').trim();
  if (!text.includes('.')) return 0;
  return text.split('.')[1].length;
}

export function parseEnteredNumber(value) {
  const text = String(value ?? '').trim();
  if (!text) return { empty: true, finite: false, number: null };
  if (!/^-?\d+(\.\d+)?$/.test(text)) return { empty: false, finite: false, number: null, text };
  const number = Number(text);
  if (!Number.isFinite(number)) return { empty: false, finite: false, number: null, text };
  return { empty: false, finite: true, number, text };
}

export function toBaseUnit(number, unit) {
  if (NON_CONVERTIBLE_UNITS.has(unit)) return null;
  const factor = UNIT_TO_BASE[unit];
  if (!Number.isFinite(number) || !factor) return null;
  return number * factor;
}

export function integersOnlyFor(purpose, unit) {
  if (!purpose) return false;
  if (purpose.bytesIntegersOnly && unit === 'bytes') return true;
  return Boolean(purpose.integersOnly);
}

export function rangeHasPermittedValue(min, max, minBound, maxBound, integersOnly) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return false;
  if (min > max) return false;
  const minIncluded = minBound === 'included';
  const maxIncluded = maxBound === 'included';
  if (min === max) return minIncluded && maxIncluded;
  if (!integersOnly) return true;
  const start = minIncluded ? Math.ceil(min) : Math.floor(min) + 1;
  const end = maxIncluded ? Math.floor(max) : Math.ceil(max) - 1;
  return start <= end;
}

export function formatLimitDisplay(rule) {
  const form = normalizeLimitForm(rule);
  if (form.limitType === 'min_max_range') {
    const minBound = labelFromOptions(BOUNDARY_OPTIONS, form.minimumBoundary) || form.minimumBoundary;
    const maxBound = labelFromOptions(BOUNDARY_OPTIONS, form.maximumBoundary) || form.maximumBoundary;
    if (form.minimum === '' || form.maximum === '') return '—';
    return `Minimum ${form.minimum} ${minBound}; Maximum ${form.maximum} ${maxBound}`;
  }
  const operator = getOperatorLabel(form.operator);
  if (!operator || form.value === '') return '—';
  return `${operator} ${form.value}`;
}

export function conditionValues(condition) {
  return [...new Set((condition?.values || []).map((value) => String(value || '').trim()).filter(Boolean))];
}

export function normalizedConditionOperator(operator) {
  if (operator === 'is') return 'is_any_of';
  if (operator === 'is_not') return 'is_not_any_of';
  return operator;
}

export function conditionFingerprint(condition) {
  return [
    condition.field || '',
    normalizedConditionOperator(condition.operator),
    conditionValues(condition).slice().sort().join(','),
  ].join('|');
}

export function equivalentConditions(a = [], b = []) {
  const left = (a || []).map(conditionFingerprint).sort().join('||');
  const right = (b || []).map(conditionFingerprint).sort().join('||');
  return left === right;
}

export function numericFingerprint(rule) {
  const form = normalizeLimitForm(rule);
  if (form.limitType === 'min_max_range') {
    const minBase = toBaseUnit(Number(form.minimum), form.unit);
    const maxBase = toBaseUnit(Number(form.maximum), form.unit);
    return [
      form.limitType,
      'within_range',
      minBase != null ? minBase : `${form.minimum}:${form.unit}`,
      maxBase != null ? maxBase : `${form.maximum}:${form.unit}`,
      form.minimumBoundary,
      form.maximumBoundary,
    ].join('|');
  }
  const valueBase = toBaseUnit(Number(form.value), form.unit);
  return [
    form.limitType,
    form.operator,
    valueBase != null ? valueBase : `${form.value}:${form.unit}`,
    valueBase != null ? 'base' : form.unit,
  ].join('|');
}

export function conditionsMutuallyExclusive(a = [], b = []) {
  if (!(a || []).length || !(b || []).length) return false;
  const fields = [...new Set([...(a || []), ...(b || [])].map((condition) => condition.field).filter(Boolean))];
  return fields.some((field) => {
    const left = (a || []).filter((condition) => condition.field === field);
    const right = (b || []).filter((condition) => condition.field === field);
    const leftPositive = left.filter((condition) => ['is', 'is_any_of'].includes(condition.operator)).flatMap(conditionValues);
    const rightPositive = right.filter((condition) => ['is', 'is_any_of'].includes(condition.operator)).flatMap(conditionValues);
    if (leftPositive.length && rightPositive.length && leftPositive.every((value) => !rightPositive.includes(value))) {
      return true;
    }
    const leftNegative = left.filter((condition) => ['is_not', 'is_not_any_of'].includes(condition.operator)).flatMap(conditionValues);
    if (leftNegative.length && rightPositive.length && rightPositive.every((value) => leftNegative.includes(value))) return true;
    const rightNegative = right.filter((condition) => ['is_not', 'is_not_any_of'].includes(condition.operator)).flatMap(conditionValues);
    if (rightNegative.length && leftPositive.length && leftPositive.every((value) => rightNegative.includes(value))) return true;
    return false;
  });
}

export function coverageOverlaps(a, b) {
  if (a.requirement !== b.requirement || a.limitFor !== b.limitFor) return false;
  if (!(a.conditions || []).length || !(b.conditions || []).length) return true;
  if (conditionsMutuallyExclusive(a.conditions, b.conditions)) return false;
  return true;
}

export function findDuplicateLimitRule(rules, candidate, excludeId) {
  const form = normalizeLimitForm(candidate);
  const numeric = numericFingerprint(form);
  return (rules || []).map(normalizeLimitForm).find((rule) => (
    rule.id !== excludeId
    && rule.requirement === form.requirement
    && rule.limitFor === form.limitFor
    && equivalentConditions(rule.conditions, form.conditions)
    && numericFingerprint(rule) === numeric
  )) || null;
}

export function findConflictingLimitRule(rules, candidate, excludeId) {
  const form = normalizeLimitForm(candidate);
  const numeric = numericFingerprint(form);
  return (rules || []).map(normalizeLimitForm).find((rule) => (
    rule.id !== excludeId
    && coverageOverlaps(rule, form)
    && numericFingerprint(rule) !== numeric
  )) || null;
}

export function findOverlappingLimitRules(rules, candidate, excludeId) {
  const form = normalizeLimitForm(candidate);
  return (rules || []).map(normalizeLimitForm).filter((rule) => (
    rule.id !== excludeId && coverageOverlaps(rule, form)
  ));
}

export function buildLimitScopeKey(rule) {
  const form = normalizeLimitForm(rule || {});
  return `${form.requirement || ''}|${form.limitFor || ''}`;
}

export function formatConditionLine(condition) {
  const field = getConditionFieldLabel(condition.field);
  const operator = getConditionOperatorLabel(condition.operator);
  const values = (condition.values || []).map((value) => getConditionValueLabel(condition.field, value));
  return [field, operator, values.join(', ')].filter(Boolean).join(' · ');
}

export function formatConditionsPreview(conditions = []) {
  if (!conditions.length) return { text: 'No Conditions', extraCount: 0, lines: [] };
  const lines = conditions.map(formatConditionLine);
  if (lines.length === 1) return { text: lines[0], extraCount: 0, lines };
  return {
    text: `${lines[0]} · +${lines.length - 1} more`,
    extraCount: lines.length - 1,
    lines,
  };
}

export function formatLimitDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
}

export function sortLimitRules(rules = []) {
  return [...rules].sort((a, b) => {
    const aTime = new Date(a.lastUpdated || a.dateAdded || 0).getTime();
    const bTime = new Date(b.lastUpdated || b.dateAdded || 0).getTime();
    return bTime - aTime;
  });
}

function actorFields(stamp) {
  return {
    addedBy: 'Sample Admin',
    lastUpdatedBy: 'Sample Admin',
    dateAdded: stamp,
    lastUpdated: stamp,
  };
}

export function buildSampleLimitRules() {
  return [
    {
      id: 'LTH-0001',
      ruleName: 'Post Record Limit',
      description: 'Maximum 50 Post records within Posts.',
      requirement: 'quantity_limit',
      limitFor: 'record_count',
      limitType: 'single_value',
      operator: 'at_most',
      value: '50',
      unit: 'count',
      conditions: [
        { id: 'c1', field: 'module', operator: 'is', values: ['posts'] },
        { id: 'c2', field: 'record_type', operator: 'is', values: ['posts:post'] },
      ],
      changeReason: 'Set the maximum Post record count to 50.',
      ...actorFields('2026-09-18T10:00:00.000Z'),
    },
    {
      id: 'LTH-0002',
      ruleName: 'Review Record Range',
      description: 'Between 10 and 100 Review records within Reviews, including both limits.',
      requirement: 'quantity_limit',
      limitFor: 'record_count',
      limitType: 'min_max_range',
      operator: 'within_range',
      minimum: '10',
      maximum: '100',
      minimumBoundary: 'included',
      maximumBoundary: 'included',
      unit: 'count',
      conditions: [
        { id: 'c1', field: 'module', operator: 'is', values: ['reviews'] },
        { id: 'c2', field: 'record_type', operator: 'is', values: ['reviews:review'] },
      ],
      changeReason: 'Set the inclusive Review record range from 10 to 100.',
      ...actorFields('2026-09-18T09:55:00.000Z'),
    },
    {
      id: 'LTH-0003',
      ruleName: 'Minimum Record Age',
      description: 'Set a minimum record age of 7 days.',
      requirement: 'duration_limit',
      limitFor: 'record_age',
      limitType: 'single_value',
      operator: 'at_least',
      value: '7',
      unit: 'days',
      conditions: [],
      changeReason: 'Set the minimum record age to 7 days.',
      ...actorFields('2026-09-18T09:50:00.000Z'),
    },
    {
      id: 'LTH-0004',
      ruleName: 'Record Share Limit',
      description: 'Set a maximum record share of 25%.',
      requirement: 'percentage_threshold',
      limitFor: 'record_share',
      limitType: 'single_value',
      operator: 'at_most',
      value: '25',
      unit: 'percent',
      conditions: [],
      changeReason: 'Set the record share limit to 25%.',
      ...actorFields('2026-09-18T09:45:00.000Z'),
    },
    {
      id: 'LTH-0005',
      ruleName: 'Location Distance Limit',
      description: 'Set a maximum location distance of 25 km for Posts.',
      requirement: 'distance_limit',
      limitFor: 'location_distance',
      limitType: 'single_value',
      operator: 'at_most',
      value: '25',
      unit: 'km',
      conditions: [{ id: 'c1', field: 'module', operator: 'is', values: ['posts'] }],
      changeReason: 'Set the location distance limit to 25 km for Posts.',
      ...actorFields('2026-09-18T09:40:00.000Z'),
    },
    {
      id: 'LTH-0006',
      ruleName: 'Total Record Size Limit',
      description: 'Set a maximum total record size of 1 GB.',
      requirement: 'size_limit',
      limitFor: 'total_record_size',
      limitType: 'single_value',
      operator: 'at_most',
      value: '1',
      unit: 'gb',
      conditions: [],
      changeReason: 'Set the total record size limit to 1 GB.',
      ...actorFields('2026-09-18T09:35:00.000Z'),
    },
    {
      id: 'LTH-0007',
      ruleName: 'Initial Send Delay',
      description: 'Initial send delay of five minutes.',
      requirement: 'duration_limit',
      limitFor: 'initial_send_delay',
      limitType: 'single_value',
      operator: 'equal_to',
      value: '5',
      unit: 'minutes',
      conditions: [],
      changeReason: 'Set the initial send delay to five minutes.',
      ...actorFields('2026-09-18T09:30:00.000Z'),
    },
    {
      id: 'LTH-0008',
      ruleName: 'Repeat Send Interval',
      description: 'Set the repeat send interval to 15 minutes.',
      requirement: 'duration_limit',
      limitFor: 'repeat_send_interval',
      limitType: 'single_value',
      operator: 'equal_to',
      value: '15',
      unit: 'minutes',
      conditions: [],
      changeReason: 'Set the repeat send interval to 15 minutes.',
      ...actorFields('2026-09-18T09:25:00.000Z'),
    },
    {
      id: 'LTH-0009',
      ruleName: 'Maximum Sends',
      description: 'Set the maximum total sends to 3.',
      requirement: 'quantity_limit',
      limitFor: 'maximum_sends',
      limitType: 'single_value',
      operator: 'at_most',
      value: '3',
      unit: 'count',
      conditions: [],
      changeReason: 'Set the maximum sends to 3.',
      ...actorFields('2026-09-18T09:20:00.000Z'),
    },
    {
      id: 'LTH-0010',
      ruleName: 'Status Change Period',
      description: 'Set the status change period to 60 minutes.',
      requirement: 'duration_limit',
      limitFor: 'status_change_period',
      limitType: 'single_value',
      operator: 'equal_to',
      value: '60',
      unit: 'minutes',
      conditions: [],
      changeReason: 'Set the status change period to 60 minutes.',
      ...actorFields('2026-09-18T09:15:00.000Z'),
    },
    {
      id: 'LTH-0011',
      ruleName: 'Status Change Retry Limit',
      description: 'Set the status change retry limit to 1.',
      requirement: 'quantity_limit',
      limitFor: 'status_change_retry_limit',
      limitType: 'single_value',
      operator: 'at_most',
      value: '1',
      unit: 'count',
      conditions: [],
      changeReason: 'Set the status change retry limit to 1.',
      ...actorFields('2026-09-18T09:10:00.000Z'),
    },
    {
      id: 'LTH-0012',
      ruleName: 'Status Change Retry Interval',
      description: 'Set the status change retry interval to 5 minutes.',
      requirement: 'duration_limit',
      limitFor: 'status_change_retry_interval',
      limitType: 'single_value',
      operator: 'equal_to',
      value: '5',
      unit: 'minutes',
      conditions: [],
      changeReason: 'Set the status change retry interval to 5 minutes.',
      ...actorFields('2026-09-18T09:05:00.000Z'),
    },
    {
      id: 'LTH-0013',
      ruleName: 'Compliance Information Response Period',
      description: 'Information-response period retained for Compliance cases.',
      requirement: 'duration_limit',
      limitFor: 'compliance_information_response_period',
      limitType: 'single_value',
      operator: 'equal_to',
      value: '14',
      unit: 'calendar_days',
      conditions: [],
      changeReason: 'Set the Compliance information-response period to 14 calendar days.',
      ...actorFields('2026-09-18T09:00:00.000Z'),
    },
    {
      id: 'LTH-0014',
      ruleName: 'Fraud & Security Information Response Period',
      description: 'Information-response period for new F&S requests.',
      requirement: 'duration_limit',
      limitFor: 'fraud_security_information_response_period',
      limitType: 'single_value',
      operator: 'equal_to',
      value: '14',
      unit: 'calendar_days',
      conditions: [],
      changeReason: 'Set the F&S information-response period to 14 calendar days.',
      ...actorFields('2026-09-18T08:55:00.000Z'),
    },
  ].map((rule) => applyLimitDerivedFields(normalizeLimitForm(rule)));
}

export function valueSatisfiesRule(measured, rule) {
  const form = normalizeLimitForm(rule);
  const measuredBase = toBaseUnit(Number(measured), form.unit);
  if (!Number.isFinite(measuredBase)) return false;
  if (form.limitType === 'min_max_range') {
    const min = toBaseUnit(Number(form.minimum), form.unit);
    const max = toBaseUnit(Number(form.maximum), form.unit);
    const minOk = form.minimumBoundary === 'included' ? measuredBase >= min : measuredBase > min;
    const maxOk = form.maximumBoundary === 'included' ? measuredBase <= max : measuredBase < max;
    return minOk && maxOk;
  }
  const value = toBaseUnit(Number(form.value), form.unit);
  if (form.operator === 'equal_to') return measuredBase === value;
  if (form.operator === 'at_least') return measuredBase >= value;
  if (form.operator === 'greater_than') return measuredBase > value;
  if (form.operator === 'at_most') return measuredBase <= value;
  if (form.operator === 'less_than') return measuredBase < value;
  return false;
}

/** @deprecated aliases */
export const LIMIT_MODULE_OPTIONS = CONDITION_MODULE_OPTIONS.map(({ value, label }) => ({ value, label }));
export const COUNTED_EVENT_OPTIONS = [];
export const MEASUREMENT_SCOPE_OPTIONS = [];
export const IDENTIFIER_TYPE_OPTIONS = [];
export const BREACH_APPLICATION_TIMING_OPTIONS = [];
export const RESET_RULE_OPTIONS = [];
export const BREACH_RESULT_OPTIONS = [];
export const REVIEW_HANDOFF_OPTIONS = [];
export const PENDING_ACTIVITY_TREATMENT_OPTIONS = [];
export const RESTRICTION_START_OPTIONS = [];
export const RELEASE_SOURCE_OPTIONS = [];

export function getLimitModuleLabel(value) {
  return labelFromOptions(LIMIT_MODULE_OPTIONS, value);
}

export function getLimitMeasureOptions() {
  return [];
}

export function getLimitMeasureLabel() {
  return '';
}

export function labelLimitMeasure() {
  return '';
}

export function getCountedEventOptions() {
  return [];
}

export function getMeasurementScopeOptions() {
  return [];
}

export function needsIdentifierType() {
  return false;
}

export function getBreachApplicationTimingOptions() {
  return [];
}

export function getReviewHandoffOptions() {
  return [];
}

export function getAffectedFunctionOptions() {
  return [];
}

export function normalizeLimitRuleStatus(status) {
  return getRuleStateLabel(status) === 'Paused' ? 'paused' : 'active';
}

export function limitRuleDuplicateKey(rule) {
  const form = normalizeLimitForm(rule);
  return [form.requirement, form.limitFor, numericFingerprint(form), (form.conditions || []).map(conditionFingerprint).sort().join('||')].join('|');
}
